/* eslint-disable no-unused-vars */
import React, {
  useState,
  useMemo,
  useEffect,
  useContext,
  useCallback,
  useRef,
} from "react";
import { useNavigate } from "react-router-dom";
import { Users, Briefcase, UserCheck, Calendar } from "lucide-react";
import { AdminContext } from "../../context/AdminContext";
import { AppContext } from "../../context/AppContext";
import {
  KPICard,
  MetricRow,
  ActivityChart,
  CategoryDistributionChart,
  TopSubcitiesChart,
  LatestBookingsTable,
} from "../../components/dashboard";
import { periodOptions } from "../../utils/constants";

const Dashboard = () => {
  const navigate = useNavigate();
  const { aToken, getDashData, dashData } = useContext(AdminContext);
  const { slotDateFormat } = useContext(AppContext);

  // State
  const [period, setPeriod] = useState("week");
  const [customDate, setCustomDate] = useState({ start: "", end: "" });
  const [bookings, setBookings] = useState([]);

  // Refs for tracking
  const prevFilterRef = useRef({});
  const fetchInProgressRef = useRef(false);
  const prevPeriodRef = useRef("week");
  const prevCustomDateRef = useRef({ start: "", end: "" });
  const prevDashDataRef = useRef(null);

  // Memoized fetch function
  const fetchDashboardData = useCallback(
    async (filter) => {
      if (!aToken || fetchInProgressRef.current) return;

      try {
        fetchInProgressRef.current = true;
        await getDashData(filter);
      } finally {
        fetchInProgressRef.current = false;
      }
    },
    [aToken, getDashData],
  );

  // Main useEffect - optimized with debouncing and change detection
  useEffect(() => {
    if (!aToken) return;

    let filter = {};
    let shouldFetch = false;

    if (period === "custom") {
      // For custom dates, DON'T auto-fetch - user must click Apply button
      // Just return early, the Apply button will call fetchDashboardData directly
      return;
    } else {
      // Only fetch if period changed
      filter = { period: period.toLowerCase() };
      if (period !== prevPeriodRef.current) {
        shouldFetch = true;
        prevPeriodRef.current = period;
      }
    }

    // Also fetch if it's the first load (no previous filter)
    if (Object.keys(prevFilterRef.current).length === 0) {
      shouldFetch = true;
    }

    if (shouldFetch && !fetchInProgressRef.current) {
      prevFilterRef.current = filter;

      // Debounce API call
      const timeoutId = setTimeout(() => {
        fetchDashboardData(filter);
      }, 350); // 350ms debounce

      return () => clearTimeout(timeoutId);
    }
  }, [period, aToken, fetchDashboardData]);

  // Memoize booking data transformation
  useEffect(() => {
    if (
      dashData &&
      (dashData.latestBookings || dashData.latestAppointments) &&
      dashData !== prevDashDataRef.current
    ) {
      const sourceBookings =
        dashData.latestBookings || dashData.latestAppointments || [];
      const transformedBookings = sourceBookings.map((item, index) => ({
        id: item.id,
        caregiver: item.careGiverData?.name || "Unknown",
        client: item.userData?.name || "N/A",
        type: item.category || "General",
        date: slotDateFormat(item.slotDate),
        status: item.status || "Pending",
        avatar: item.careGiverData?.image,
      }));
      setBookings(transformedBookings);
      prevDashDataRef.current = dashData;
    }
  }, [dashData, slotDateFormat]);

  // Prepare chart data for activity overview - memoized
  const chartData = useMemo(() => {
    if (!dashData) return [];

    const funnel = dashData.bookingFunnel || dashData.appointmentFunnel || {};
    return [
      {
        name: period === "custom" ? "Custom" : `This ${period}`,
        bookings: funnel.totalBookings || funnel.totalAppointments || 0,
        completed: funnel.completed || funnel.completedBookings || 0,
        workStarted: funnel.workStarted || funnel.workStartedBookings || 0,
        cancelled: funnel.cancelled || funnel.cancelledBookings || 0,
      },
    ];
  }, [dashData, period]);

  // Prepare category distribution data - memoized
  const categoryChartData = useMemo(() => {
    if (!dashData?.categoryStats) return [];

    return dashData.categoryStats.map((stat) => ({
      category: stat.category,
      name: stat.category,
      count: stat.caregivers || 0,
    }));
  }, [dashData]);

  // Prepare subcity data - memoized
  const subcityChartData = useMemo(() => {
    if (!dashData?.subcityStats) return [];

    return dashData.subcityStats
      .filter((sub) => sub.subcity)
      .map((sub) => ({
        name: sub.subcity,
        value: sub.totalActivity || sub.bookings || sub.appointments || 0,
        jobs: sub.jobs || 0,
        bookings: sub.bookings ?? sub.appointments ?? 0,
        totalActivity: sub.totalActivity || 0,
      }))
      .sort((a, b) => b.totalActivity - a.totalActivity)
      .slice(0, 5);
  }, [dashData]);

  // Calculate total caregivers for percentages
  const totalCaregivers = dashData?.totalCaregivers || 0;

  // Calculate percentages - memoized
  const getPercentage = useCallback((part, total) => {
    if (!total || total === 0) return "0%";
    return Math.round((part / total) * 100) + "%";
  }, []);

  // Handle custom date changes
  const handleCustomDateChange = useCallback((field, value) => {
    setCustomDate((prev) => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  // Handle period change
  const handlePeriodChange = useCallback((newPeriod) => {
    setPeriod(newPeriod);
    // Reset custom date when switching from custom to other periods
    if (newPeriod !== "custom") {
      setCustomDate({ start: "", end: "" });
    }
  }, []);

  // Handle view all bookings
  const handleViewAllBookings = useCallback(() => {
    navigate("/bookings");
  }, [navigate]);

  if (!dashData) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 pb-6 sm:pb-8 md:pb-12 relative">
      {/* Header & Filter */}
      <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-4 sm:gap-6 mb-6 sm:mb-8">
        <div>
          <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-text dark:text-dark-text">
            Flows & Analytics
          </h1>
          <p className="text-gray-500 dark:text-dark-muted mt-1 text-xs sm:text-sm">
            Comprehensive overview of platform performance and metrics.
          </p>
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          {period === "custom" && (
            <div className="flex items-center gap-2 bg-card dark:bg-dark-surface p-1 rounded-xl border border-gray-100 dark:border-dark-border shadow-sm">
              <div className="relative">
                <input
                  type="date"
                  value={customDate.start}
                  onChange={(e) =>
                    handleCustomDateChange("start", e.target.value)
                  }
                  className="pl-2 pr-1 py-1.5 text-xs sm:text-sm bg-transparent font-medium text-gray-700 dark:text-dark-text outline-none w-28 sm:w-32"
                />
              </div>
              <span className="text-gray-400 dark:text-dark-muted text-xs">
                -
              </span>
              <div className="relative">
                <input
                  type="date"
                  value={customDate.end}
                  onChange={(e) =>
                    handleCustomDateChange("end", e.target.value)
                  }
                  className="pl-1 pr-2 py-1.5 text-xs sm:text-sm bg-transparent font-medium text-gray-700 dark:text-dark-text outline-none w-28 sm:w-32"
                />
              </div>
              <button
                onClick={() => {
                  if (customDate.start && customDate.end) {
                    fetchDashboardData({
                      start: customDate.start,
                      end: customDate.end,
                    });
                  }
                }}
                disabled={!customDate.start || !customDate.end}
                className={`ml-1 px-3 py-1.5 rounded-lg text-xs sm:text-sm font-semibold transition-all ${
                  customDate.start && customDate.end
                    ? "bg-primary text-white hover:bg-primary/90 shadow-md shadow-primary/20"
                    : "bg-gray-200 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed"
                }`}
              >
                Apply
              </button>
            </div>
          )}

          <div className="flex bg-card dark:bg-dark-surface p-1 rounded-xl border border-gray-100 dark:border-dark-border shadow-sm w-fit">
            {periodOptions.map((option) => (
              <button
                key={option.value}
                onClick={() => handlePeriodChange(option.value)}
                className={`px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-medium transition-all ${
                  period === option.value
                    ? "bg-primary text-white shadow-md shadow-primary/20 dark:shadow-none"
                    : "text-gray-600 dark:text-dark-text hover:bg-gray-50 dark:hover:bg-gray-700"
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-6 sm:mb-8">
        <KPICard
          title="Total Caregivers"
          count={dashData.totalCaregivers?.toLocaleString() || "0"}
          change={dashData.comparison?.caregivers || 0}
          isPositive={(dashData.comparison?.caregivers || 0) >= 0}
          icon={Users}
          color="text-primary"
          bgClass="bg-primary border-primary/20"
        />
        <KPICard
          title="Total Clients"
          count={dashData.totalUsers?.toLocaleString() || "0"}
          change={dashData.comparison?.users || 0}
          isPositive={(dashData.comparison?.users || 0) >= 0}
          icon={UserCheck}
          color="text-blue-600"
          bgClass="bg-gradient-to-r from-primary to-secondary border-primary/20"
        />
        <KPICard
          title="Active Contracts"
          count={dashData.activeContracts?.toLocaleString() || "0"}
          change={0}
          isPositive={true}
          icon={Briefcase}
          color="text-purple-600"
          bgClass="bg-gradient-to-r from-primary to-secondary border-primary/20"
        />
        <KPICard
          title="Platform Revenue"
          count={`ETB ${(dashData.platformRevenue?.monthlyFee || 0).toLocaleString()}`}
          change={dashData.platformRevenue?.change || 0}
          isPositive={(dashData.platformRevenue?.change || 0) >= 0}
          icon={Calendar}
          color="text-orange-600"
          bgClass="bg-slate-900 border-slate-800"
        />
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 mb-6 sm:mb-8">
        {/* Caregiver Funnel - Compact */}
        <div className="bg-slate-900 p-4 sm:p-5 rounded-2xl border border-slate-800 shadow-sm text-white">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-sm sm:text-base">Caregiver Funnel</h3>
            <span className="text-xs px-2 py-1 bg-blue-500/20 text-blue-300 rounded border border-blue-500/30">
              Time-filtered
            </span>
          </div>
          <div className="space-y-0.5">
            <MetricRow
              label="Total Caregivers"
              count={
                dashData.caregiverFunnel?.totalCaregivers?.toLocaleString() ||
                "0"
              }
              percentage="100%"
              color="bg-primary"
            />
            <MetricRow
              label="Onboarded"
              count={
                dashData.caregiverFunnel?.onboardedCaregivers?.toLocaleString() ||
                "0"
              }
              percentage={getPercentage(
                dashData.caregiverFunnel?.onboardedCaregivers,
                dashData.caregiverFunnel?.totalCaregivers,
              )}
              color="bg-emerald-500"
            />
            <MetricRow
              label="Applications"
              count={
                dashData.caregiverFunnel?.totalApplications?.toLocaleString() ||
                "0"
              }
              percentage={getPercentage(
                dashData.caregiverFunnel?.totalApplications,
                dashData.caregiverFunnel?.totalCaregivers,
              )}
              color="bg-blue-500"
            />
            <MetricRow
              label="Jobs Applied"
              count={
                dashData.caregiverFunnel?.jobsApplied?.toLocaleString() || "0"
              }
              percentage={getPercentage(
                dashData.caregiverFunnel?.jobsApplied,
                dashData.caregiverFunnel?.totalApplications,
              )}
              color="bg-purple-500"
            />
          </div>
        </div>

        {/* Booking Funnel - Compact */}
        <div className="bg-slate-900 p-4 sm:p-5 rounded-2xl border border-slate-800 shadow-sm text-white">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-sm sm:text-base">Booking Funnel</h3>
            <span className="text-xs px-2 py-1 bg-blue-500/20 text-blue-300 rounded border border-blue-500/30">
              Time-filtered
            </span>
          </div>
          <div className="space-y-0.5">
            <MetricRow
              label="Total Bookings"
              count={
                (
                  dashData.bookingFunnel?.totalBookings ||
                  dashData.appointmentFunnel?.totalAppointments
                )?.toLocaleString() || "0"
              }
              percentage="100%"
              color="bg-blue-500"
            />
            <MetricRow
              label="Completed"
              count={
                (
                  dashData.bookingFunnel?.completed ||
                  dashData.appointmentFunnel?.completed ||
                  dashData.bookingFunnel?.completedBookings
                )?.toLocaleString() || "0"
              }
              percentage={getPercentage(
                dashData.bookingFunnel?.completed ||
                  dashData.appointmentFunnel?.completed ||
                  dashData.bookingFunnel?.completedBookings,
                dashData.bookingFunnel?.totalBookings ||
                  dashData.appointmentFunnel?.totalAppointments,
              )}
              color="bg-emerald-500"
            />
            <MetricRow
              label="Work Started"
              count={
                (
                  dashData.bookingFunnel?.workStarted ||
                  dashData.appointmentFunnel?.workStarted ||
                  dashData.bookingFunnel?.workStartedBookings
                )?.toLocaleString() || "0"
              }
              percentage={getPercentage(
                dashData.bookingFunnel?.workStarted ||
                  dashData.appointmentFunnel?.workStarted ||
                  dashData.bookingFunnel?.workStartedBookings,
                dashData.bookingFunnel?.totalBookings ||
                  dashData.appointmentFunnel?.totalAppointments,
              )}
              color="bg-yellow-500"
            />
            <MetricRow
              label="Cancelled"
              count={
                (
                  dashData.bookingFunnel?.cancelled ||
                  dashData.appointmentFunnel?.cancelled ||
                  dashData.bookingFunnel?.cancelledBookings
                )?.toLocaleString() || "0"
              }
              percentage={getPercentage(
                dashData.bookingFunnel?.cancelled ||
                  dashData.appointmentFunnel?.cancelled ||
                  dashData.bookingFunnel?.cancelledBookings,
                dashData.bookingFunnel?.totalBookings ||
                  dashData.appointmentFunnel?.totalAppointments,
              )}
              color="bg-red-500"
            />
          </div>
          <div className="mt-4 pt-4 border-t border-slate-700 grid grid-cols-2 gap-3">
            <div>
              <p className="text-xs text-white/60 uppercase font-bold">
                Open Jobs
              </p>
              <p className="text-lg font-bold text-white">
                {dashData.serviceRequestMetrics?.openJobs || 0}
              </p>
            </div>
            <div className="text-right">
              <p className="text-xs text-white/60 uppercase font-bold">
                Closed Jobs
              </p>
              <p className="text-lg font-bold text-white">
                {dashData.serviceRequestMetrics?.closedJobs || 0}
              </p>
            </div>
          </div>
        </div>

        {/* Category Distribution */}
        <CategoryDistributionChart
          data={categoryChartData}
          totalCaregivers={totalCaregivers}
        />
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-6 sm:mb-8">
        {/* Activity Chart */}
        <ActivityChart data={chartData} period={period} />

        {/* Top Subcities */}
        <TopSubcitiesChart data={subcityChartData} />
      </div>

      {/* Latest Bookings Table */}
      <LatestBookingsTable
        bookings={bookings}
        onViewAll={handleViewAllBookings}
      />
    </div>
  );
};

export default Dashboard;
